import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, TextInput, TextArea, Select, SelectItem, InlineNotification } from '@carbon/react';
import {
  Email,
  Location,
  Send,
  Chat,
  Time,
  CheckmarkFilled,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { submitContactForm } from '../services/contactService';

const ContactPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState({ name: '', email: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  
  // Initialize form with user's email if authenticated
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user?.email]);

  // Prefill and scroll when arriving from Contact Sales / product pages
  useEffect(() => {
    const state = location.state;
    if (!state) return;

    if (state.subject || state.message || state.productName) {
      setFormData((prev) => ({
        ...prev,
        subject: state.subject || prev.subject,
        message: state.message || prev.message,
      }));
    }

    if (state.scrollToForm) {
      requestAnimationFrame(() => {
        document.querySelector('[data-testid="contact-form"]')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [location.state]);

  const contactInfo = [
    { icon: Email, title: 'Email', value: 'support@chillverify.com', link: 'mailto:support@chillverify.com' },
    { icon: Location, title: 'Location', value: 'Tasmania, Australia', link: '#' },
  ];

  const supportOptions = [
    { title: 'Technical Support', desc: '24/7 support for technical issues', time: '< 1 hour response' },
    { title: 'Sales Inquiry', desc: 'Pricing and custom solutions', time: '< 4 hours response' },
    { title: 'Partnership', desc: 'Integration and partnership opportunities', time: '< 24 hours response' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    // Check authentication first
    if (!isAuthenticated) {
      setSubmitError('You must be logged in to submit the contact form.');
      navigate('/login');
      return;
    }
    
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await submitContactForm(formData);
      
      if (error) {
        setSubmitError(error.message || 'Failed to send message. Please try again.');
        setIsSubmitting(false);
        return;
      }
      
      // Success - save submitted data before clearing form
      setSubmittedData({ name: formData.name, email: formData.email });
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setErrors({});
      setIsSubmitting(false);
    } catch (err) {
      console.error('Contact form submission error:', err);
      setSubmitError(err.message || 'An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="contact-page">
      {/* Hero */}
      <section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }}>
        <Grid>
          <Column lg={10} md={6} sm={4}>
            <div style={{ color: '#ffffff' }}>
              <Tag type="blue" style={{ marginBottom: '16px' }}>Contact Us</Tag>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '24px' }}>
                Get in Touch
              </h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Contact Form & Support Options */}
      <section id="contact-form" style={{ padding: '80px 0' }}>
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <h2 style={{ color: textPrimary, marginBottom: '24px', fontSize: '2rem', fontWeight: 600 }}>
              Send us a Message
            </h2>
            
            {!isAuthenticated && (
              <InlineNotification
                kind="warning"
                title="Authentication Required"
                subtitle="You must be logged in to submit the contact form. Please sign in to continue."
                actionButtonLabel="Sign In"
                onActionButtonClick={() => navigate('/login')}
                style={{ marginBottom: '24px' }}
                data-testid="auth-required-notification"
              />
            )}
            
            {submitted && (
              <InlineNotification
                kind="success"
                title="Message sent!"
                subtitle={`Thank you ${submittedData.name || 'there'}! We've received your message and will reach out to ${submittedData.email || 'your email'} soon.`}
                onClose={() => {
                  setSubmitted(false);
                  setSubmittedData({ name: '', email: '' });
                }}
                style={{ marginBottom: '24px' }}
                data-testid="success-notification"
              />
            )}
            
            {submitError && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={submitError}
                onClose={() => setSubmitError('')}
                style={{ marginBottom: '24px' }}
                data-testid="error-notification"
              />
            )}
            
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div style={{ marginBottom: '24px' }}>
                <TextInput
                  id="name"
                  name="name"
                  labelText="Name *"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  invalid={!!errors.name}
                  invalidText={errors.name}
                  disabled={!isAuthenticated}
                  data-testid="contact-name"
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  labelText="Email *"
                  placeholder={user?.email || "your@email.com"}
                  value={formData.email || user?.email || ''}
                  onChange={handleChange}
                  invalid={!!errors.email}
                  invalidText={errors.email}
                  disabled={!isAuthenticated}
                  data-testid="contact-email"
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <TextInput
                  id="company"
                  name="company"
                  labelText="Company"
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                  data-testid="contact-company"
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <Select
                  id="subject"
                  name="subject"
                  labelText="Subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  disabled={!isAuthenticated}
                  data-testid="contact-subject"
                >
                  <SelectItem value="" text="Select a subject" />
                  <SelectItem value="technical" text="Technical Support" />
                  <SelectItem value="sales" text="Sales Inquiry" />
                  <SelectItem value="partnership" text="Partnership" />
                  <SelectItem value="other" text="Other" />
                </Select>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <TextArea
                  id="message"
                  name="message"
                  labelText="Message *"
                  placeholder="How can we help you?"
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  invalid={!!errors.message}
                  invalidText={errors.message}
                  disabled={!isAuthenticated}
                  data-testid="contact-message"
                />
              </div>
              <Button 
                kind="primary" 
                type="submit" 
                renderIcon={Send} 
                disabled={!isAuthenticated || isSubmitting}
                data-testid="contact-submit"
              >
                {isSubmitting ? 'Sending...' : isAuthenticated ? 'Send Message' : 'Sign In to Send'}
              </Button>
            </form>
          </Column>
          
          <Column lg={8} md={4} sm={4}>
            <h2 style={{ color: textPrimary, marginBottom: '24px', fontSize: '2rem', fontWeight: 600 }}>
              Support Options
            </h2>
            {supportOptions.map((option, index) => (
              <Tile
                key={index}
                className="feature-card"
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  padding: '24px',
                  marginBottom: '16px',
                }}
                data-testid={`support-option-${index}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: textPrimary, marginBottom: '8px', fontWeight: 600 }}>
                      {option.title}
                    </h4>
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                      {option.desc}
                    </p>
                  </div>
                  <div style={{ 
                    backgroundColor: '#24a148', 
                    color: '#ffffff',
                    padding: '6px 12px', 
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}>
                    <Time size={14} />
                    <span>{option.time}</span>
                  </div>
                </div>
              </Tile>
            ))}
            
            <Tile
              style={{
                backgroundColor: isDark ? '#393939' : '#e0e0e0',
                padding: '32px',
                marginTop: '32px',
                color: textSecondary,
                opacity: 0.6,
                cursor: 'not-allowed',
              }}
              data-testid="live-chat"
            >
              <Chat size={32} style={{ marginBottom: '16px', color: textSecondary, opacity: 0.5 }} />
              <h3 style={{ marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600, color: textSecondary }}>
                Live Chat
              </h3>
              <p style={{ marginBottom: '20px', opacity: 0.7, fontSize: '0.875rem', color: textSecondary }}>
                Live chat is currently unavailable. Please use the contact form or email us for support.
              </p>
              <Button 
                kind="secondary" 
                size="sm" 
                disabled 
                data-testid="start-chat"
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                Start Chat
              </Button>
            </Tile>
          </Column>
        </Grid>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: bgSecondary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: textPrimary, marginBottom: '16px', fontSize: '2rem', fontWeight: 600 }}>
                Frequently Asked Questions
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px' }}>
                Can't find the answer you're looking for? Contact our support team.
              </p>
              <Button kind="tertiary" onClick={() => navigate('/features')} data-testid="view-faq">
                View Full FAQ
              </Button>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default ContactPage;
